/**
 * @file Inline account menu — avatar trigger plus dropdown for the
 *       signed-in chrome.
 * @module @labcraft/ui/chrome/account-menu
 *
 * Designed to slot into the `accountMenu` prop of {@link Topbar}.
 * Renders the user's provider avatar when present, falling back to
 * derived initials. Click toggles a small dropdown anchored to the
 * trigger; the dropdown closes on outside-click or Escape. No router
 * or service-container coupling — callers wire the two callbacks
 * (`onSignOut`, `onUpdateAccount`) to whatever their host app needs.
 *
 * The theme toggle lives inside this dropdown — anonymous users have
 * no theme affordance by design (the choice is gated on being signed
 * in; see `ThemeToggle` for the cookie persistence).
 */

import {
  useEffect,
  useRef,
  useState,
  type JSX,
  type SyntheticEvent,
} from 'react'
import { ThemeToggle } from './theme_toggle.tsx'
import styles from './account_menu.module.css'

/**
 * Minimal user shape the account menu needs to render its trigger
 * and header. A superset of any host-app user model; the lab and
 * admin surfaces both project their `AccountUser` onto this.
 *
 * @property displayName - Human-readable name shown in the dropdown
 *   header. Used by the initials fallback when no avatar resolves.
 * @property email - Email shown in the dropdown header. Used as the
 *   secondary source for the initials fallback.
 * @property avatarUrl - Provider-issued avatar URL, or null/undefined
 *   when none. The component handles broken-image fallback internally.
 */
export interface AccountMenuUser {
  displayName: string
  email: string
  avatarUrl?: string | null
}

/**
 * Props for the {@link AccountMenu} component.
 *
 * @property user - The signed-in user. Required — render-anonymous
 *   variants should omit `AccountMenu` entirely.
 * @property onSignOut - Invoked when the user clicks "Sign out". May
 *   return a promise; the menu closes synchronously before awaiting.
 *   The caller is responsible for any post-signout navigation.
 * @property onUpdateAccount - Invoked when the user clicks "Update
 *   account". The caller is responsible for any navigation. Omit on
 *   hosts that don't have a per-user profile page (e.g. admin) — the
 *   menu item is hidden when this prop is absent.
 */
export interface AccountMenuProps {
  user: AccountMenuUser
  onSignOut: () => void | Promise<void>
  onUpdateAccount?: () => void
}

/**
 * The signed-in chrome's right-hand widget. Avatar (or initials)
 * trigger that opens a small panel containing the user header plus
 * the actions.
 */
export function AccountMenu(props: AccountMenuProps): JSX.Element {
  const [open, setOpen] = useState(false)
  const [imgFailed, setImgFailed] = useState(false)
  const rootRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open) return
    function onDocClick(e: MouseEvent): void {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  // Reset the broken-image flag whenever the URL itself changes —
  // e.g., the user updated their provider account and a fresh
  // user record carries a new `avatarUrl`.
  useEffect(() => {
    setImgFailed(false)
  }, [props.user.avatarUrl])

  async function onSignOut(): Promise<void> {
    setOpen(false)
    await props.onSignOut()
  }

  function onUpdate(e: SyntheticEvent): void {
    e.preventDefault()
    setOpen(false)
    props.onUpdateAccount?.()
  }

  const showAvatar = props.user.avatarUrl != null && !imgFailed
  const initials = deriveInitials(props.user)

  return (
    <div className={styles.accountMenu} ref={rootRef}>
      <button
        className={styles.avatarTrigger}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        onClick={() => setOpen(o => !o)}>
        {showAvatar ? (
          <img
            className={styles.avatarImg}
            // `referrerpolicy="no-referrer"` so the request to
            // Google/GitHub doesn't leak the user's current path.
            referrerPolicy="no-referrer"
            src={props.user.avatarUrl!}
            alt=""
            onError={() => setImgFailed(true)}
          />
        ) : (
          initials
        )}
      </button>
      {open && (
        <div className={styles.menuPanel} role="menu">
          <div className={styles.menuHeader}>
            <p className={styles.menuHeaderName}>{props.user.displayName}</p>
            <p className={styles.menuHeaderEmail}>{props.user.email}</p>
          </div>
          <div className={styles.themeRow}>
            <span className={styles.themeLabel}>Theme</span>
            <ThemeToggle />
          </div>
          {props.onUpdateAccount && (
            <button
              className={styles.menuItem}
              type="button"
              role="menuitem"
              onClick={onUpdate}>
              Update account
            </button>
          )}
          <button
            className={styles.menuItem}
            type="button"
            role="menuitem"
            onClick={onSignOut}>
            Sign out
          </button>
        </div>
      )}
    </div>
  )
}

/**
 * Pick a two-character initials string for the avatar fallback.
 * Tries word-initials from `displayName` first; falls back to the
 * first two characters of the email local-part. All upper-cased.
 *
 * @param user - User to derive initials for.
 * @returns Two-character initials.
 */
function deriveInitials(user: AccountMenuUser): string {
  const name = user.displayName.trim()
  if (name.length > 0) {
    const words = name.split(/\s+/).filter(w => w.length > 0)
    if (words.length >= 2) {
      return (words[0]![0]! + words[1]![0]!).toUpperCase()
    }
    if (words.length === 1) {
      const w = words[0]!
      return (w[0]! + (w[1] ?? '')).toUpperCase()
    }
  }
  const local = user.email.split('@')[0] ?? ''
  return ((local[0] ?? '?') + (local[1] ?? '')).toUpperCase()
}
